import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Collapse,
  IconButton
} from "@mui/material";
import { ExpandLess, ExpandMore, Close } from "@mui/icons-material";
import { styled } from "@mui/material/styles";

import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import messages from "../../../assets/locale/messages";
import coloredLogo from "../../../assets/images/colored-logo.png";
import {
  CategoriesIcon,
  ProductsIcon,
  SubCategoriesIcon,
  UsersIcon,
  VerificationListIcon,
  EmployeesIcon,
  RolesIcon
} from "../../../utils/Icons/SidemenuIcons";
import { PERMISSIONS_IDS, hasPermission } from "../../../utils/Roles";
import "./SideMenu.scss";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar
}));

const SideMenu = ({
  closeMobileSideMenu,
  drawerWidth,
  footerHeight,
  mobileSideMenuOpen
}) => {
  const location = useLocation();

  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];
  const [path, setPath] = useState("");
  const [openItemId, setOpenItemId] = useState(2);

  const handleItemClick = (itemId) => {
    if (openItemId === itemId) {
      setOpenItemId(null);
    } else {
      setOpenItemId(itemId);
    }
  };

  useEffect(() => {
    setPath(location.pathname);
  }, [location, setPath]);

  const activeRoute = (activekeys) => {
    return activekeys?.some((key) => path.includes(key));
  };

  const sideMenuItems = [
    (hasPermission(PERMISSIONS_IDS.product_view) ||
      hasPermission(PERMISSIONS_IDS.product_request_view)) && {
      id: 1,
      title: shared.sideMenu.products,
      icon: <ProductsIcon />,
      iconActive: <ProductsIcon active />,
      activekeys: ["products"],
      hasChildren: true,
      children: [
        hasPermission(PERMISSIONS_IDS.product_view) && {
          id: 1.1,
          title: shared.sideMenu.approvedList,
          slug: ROUTE_PATHS.approvedProducts,
          activekeys: ["approved-products"]
        },
        hasPermission(PERMISSIONS_IDS.product_request_view) && {
          id: 1.2,
          title: shared.sideMenu.requestsList,
          slug: ROUTE_PATHS.productsRequests,
          activekeys: ["review-requests"]
        }
      ]
    },
    (hasPermission(PERMISSIONS_IDS.category_view) ||
      hasPermission(PERMISSIONS_IDS.category_request_view)) && {
      id: 2,
      title: shared.sideMenu.categories,
      icon: <CategoriesIcon />,
      iconActive: <CategoriesIcon active />,
      activekeys: ["categories"],
      hasChildren: true,
      children: [
        hasPermission(PERMISSIONS_IDS.category_view) && {
          id: 2.1,
          title: shared.sideMenu.approvedList,
          slug: ROUTE_PATHS.approvedCategories,
          activekeys: ["approved-categories"]
        },
        hasPermission(PERMISSIONS_IDS.category_request_view) && {
          id: 2.2,
          title: shared.sideMenu.requestsList,
          slug: ROUTE_PATHS.categoriesRequests,
          activekeys: ["review-categories"]
        }
      ]
    },
    hasPermission(PERMISSIONS_IDS.sub_category_view) && {
      id: 3,
      title: shared.sideMenu.subCategories,
      icon: <SubCategoriesIcon />,
      iconActive: <SubCategoriesIcon active />,
      activekeys: ["sub-category"],
      slug: ROUTE_PATHS.subCategories,
      hasChildren: false
    },
    (hasPermission(PERMISSIONS_IDS.users_exporter_view) ||
      hasPermission(PERMISSIONS_IDS.users_importer_view)) && {
      id: 4,
      title: shared.sideMenu.users,
      icon: <UsersIcon />,
      iconActive: <UsersIcon active />,
      activekeys: ["users"],
      hasChildren: true,
      children: [
        hasPermission(PERMISSIONS_IDS.users_exporter_view) && {
          id: 4.1,
          title: shared.sideMenu.exportersList,
          slug: ROUTE_PATHS.usersExporters,
          activekeys: ["exporters"]
        },
        hasPermission(PERMISSIONS_IDS.users_importer_view) && {
          id: 4.2,
          title: shared.sideMenu.importersList,
          slug: ROUTE_PATHS.usersImporters,
          activekeys: ["importers"]
        },
        hasPermission(PERMISSIONS_IDS.users_exporter_view) && {
          id: 4.1,
          title: shared.sideMenu.combinedList,
          slug: ROUTE_PATHS.usersCombined,
          activekeys: ["combined"]
        }
      ]
    },
    (hasPermission(
      PERMISSIONS_IDS.verification_request_offplatform_view
    ) ||
      hasPermission(
        PERMISSIONS_IDS.verification_request_online_view
      ) ||
      hasPermission(
        PERMISSIONS_IDS.verification_request_banktransfer_view
      ) ||
      hasPermission(PERMISSIONS_IDS.transactions_view) ||
      hasPermission(PERMISSIONS_IDS.vouchers_view)) && {
      id: 5,
      title: shared.sideMenu.verificationSubscription,
      icon: <VerificationListIcon />,
      iconActive: <VerificationListIcon active />,
      activekeys: ["verification-subscription"],
      hasChildren: true,
      children: [
        (hasPermission(
          PERMISSIONS_IDS.verification_request_offplatform_view
        ) ||
          hasPermission(
            PERMISSIONS_IDS.verification_request_online_view
          ) ||
          hasPermission(
            PERMISSIONS_IDS.verification_request_banktransfer_view
          )) && {
          id: 5.1,
          title: shared.sideMenu.verificationList,
          slug: ROUTE_PATHS.verificationRequests,
          activekeys: ["verification-requests"]
        },
        hasPermission(PERMISSIONS_IDS.transactions_view) && {
          id: 5.2,
          title: shared.sideMenu.transactions,
          slug: ROUTE_PATHS.transactions,
          activekeys: ["transaction"]
        },
        hasPermission(PERMISSIONS_IDS.vouchers_view) && {
          id: 5.3,
          title: shared.sideMenu.vouchers,
          slug: ROUTE_PATHS.vouchers,
          activekeys: ["voucher"]
        }
      ]
    },
    hasPermission(PERMISSIONS_IDS.roles_view) && {
      id: 8,
      title: shared.sideMenu.roles,
      icon: <RolesIcon />,
      iconActive: <RolesIcon active />,
      activekeys: ["roles"],
      slug: ROUTE_PATHS.roles,
      hasChildren: false
    },
    hasPermission(PERMISSIONS_IDS.employees_view) && {
      id: 9,
      title: shared.sideMenu.employees,
      icon: <EmployeesIcon />,
      iconActive: <EmployeesIcon active />,
      activekeys: ["employees"],
      hasChildren: true,
      children: [
        {
          id: 9.1,
          title: shared.sideMenu.employeesList,
          slug: ROUTE_PATHS.employeesList,
          activekeys: ["/employees/list"]
        }
      ]
    }
  ];

  const renderSideMenuItems = (device) => (
    <div className="side-menu-container pt-4 scroll-within">
      <DrawerHeader>
        {device === "mobile" && (
          <div className="d-flex align-items-center justify-content-between w-100">
            <img src={coloredLogo} className="mobile-logo" />
            <IconButton
              onClick={closeMobileSideMenu}
              className="close-side-menu-icon">
              <Close />
            </IconButton>
          </div>
        )}
      </DrawerHeader>
      <List className="side-menu-list">
        {sideMenuItems.map(
          (item, index) =>
            item?.id &&
            (item?.hasChildren ? (
              <div key={`side-menu-item-${index}`}>
                <ListItem
                  disablePadding
                  sx={{ display: "block" }}
                  className="list-item"
                  onClick={() => handleItemClick(item?.id)}>
                  <ListItemButton
                    selected={activeRoute(item?.activekeys)}
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5
                    }}
                    className="list-item-wrapper">
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: "center"
                      }}>
                      {activeRoute(item?.activekeys)
                        ? item?.iconActive
                        : item?.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item?.title}
                      sx={{
                        display: "block",
                        textAlign: "start"
                      }}
                      className="list-item-label"
                    />
                    {!!openItemId && openItemId === item?.id ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse
                  in={!!openItemId && openItemId === item?.id}
                  timeout="auto"
                  unmountOnExit>
                  <List
                    component="div"
                    disablePadding
                    className="child-sub-list ">
                    {item?.children.map(
                      (child, index) =>
                        child?.id && (
                          <Link
                            to={child.slug}
                            key={`side-menu-item-${index}`}>
                            <ListItem
                              disablePadding
                              sx={{ display: "block" }}
                              className="list-item child-list-item">
                              <ListItemButton
                                selected={activeRoute(
                                  child.activekeys
                                )}
                                sx={{
                                  minHeight: 48,
                                  justifyContent: "initial",
                                  px: 2.5
                                }}
                                className={`list-item-wrapper `}>
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 1,
                                    justifyContent: "center"
                                  }}>
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.title}
                                  sx={{
                                    display: "block",
                                    textAlign: "start"
                                  }}
                                  className="list-item-label"
                                />
                              </ListItemButton>
                            </ListItem>
                          </Link>
                        )
                    )}
                  </List>
                </Collapse>
              </div>
            ) : (
              <Link to={item?.slug} key={`side-menu-item-${index}`}>
                <ListItem
                  disablePadding
                  sx={{ display: "block" }}
                  className="list-item">
                  <ListItemButton
                    selected={activeRoute(item?.activekeys)}
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5
                    }}
                    className="list-item-wrapper ">
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: "center"
                      }}>
                      {activeRoute(item?.activekeys)
                        ? item?.iconActive
                        : item?.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item?.title}
                      sx={{
                        display: "block",
                        textAlign: "start"
                      }}
                      className="list-item-label"
                    />
                  </ListItemButton>
                </ListItem>
              </Link>
            ))
        )}
      </List>
    </div>
  );

  const desktopMenuCustomStyle = {
    display: { xs: "none", md: "block" },
    "& .MuiDrawer-paper": {
      position: "relative",
      whiteSpace: "nowrap",
      width: drawerWidth,
      transition: (theme) =>
        theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen
        }),
      boxSizing: "border-box"
    }
  };

  const mobileMenuCustomStyle = {
    display: { xs: "block", md: "none" },
    "& .MuiDrawer-paper": {
      boxSizing: "border-box",
      width: drawerWidth
    }
  };

  return (
    <Box
      component="nav"
      aria-label="mailbox folders"
      className="side-menu position-fixed">
      <Drawer
        variant="temporary"
        open={mobileSideMenuOpen}
        onClose={closeMobileSideMenu}
        ModalProps={{
          keepMounted: true
        }}
        sx={mobileMenuCustomStyle}
        className="mobile-side-menu"
        anchor={lang === "en" ? "left" : "right"}>
        {renderSideMenuItems("mobile")}
      </Drawer>
      <Drawer
        style={{
          height: `calc( 100vh - ${footerHeight}px )`
        }}
        variant="permanent"
        open={true}
        sx={desktopMenuCustomStyle}
        className="desktop-side-menu">
        {renderSideMenuItems("desktop")}
      </Drawer>
    </Box>
  );
};

SideMenu.propTypes = {
  closeMobileSideMenu: PropTypes.func.isRequired,
  drawerWidth: PropTypes.number.isRequired,

  footerHeight: PropTypes.number,
  mobileSideMenuOpen: PropTypes.bool.isRequired
};

export default SideMenu;
